/**
 * RateSellerButton - Botão e modal para avaliar vendedor no marketplace
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Botão para abrir modal de avaliação
 * - Sistema de estrelas interativo
 * - Comentário opcional
 * - Feedback visual de sucesso/erro
 * - Portal para evitar problemas de re-renderização
 * 
 * Dependências:
 * - ReactDOM.createPortal
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Card, Button, Textarea (shadcn/ui)
 */

import React, { useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RateSellerButton = ({ sellerId, sellerName, itemId }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (rating === 0) {
      setFeedback({ type: 'error', message: 'Por favor, selecione uma avaliação de 1 a 5 estrelas' });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });
    
    try {
      await axios.post(`${API}/seller/${sellerId}/rate`, {
        seller_id: sellerId,
        item_id: itemId,
        rating: rating,
        comment: comment || null
      });
      setFeedback({ type: 'success', message: 'Avaliação enviada com sucesso! Obrigado pelo feedback.' });
      // Fechar modal após 2 segundos de sucesso
      setTimeout(() => {
        setShowModal(false);
        setRating(0);
        setHoverRating(0);
        setComment('');
        setFeedback({ type: '', message: '' });
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar avaliação';
      setFeedback({ type: 'error', message: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  const handleCloseModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowModal(false);
    setRating(0);
    setHoverRating(0);
    setComment('');
    setFeedback({ type: '', message: '' });
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal(e);
    }
  };

  // Modal usando createPortal para evitar problemas de re-renderização
  const modalContent = showModal ? ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={handleBackdropClick}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Avaliar Vendedor</h2>
            <Button variant="ghost" size="sm" onClick={handleCloseModal}>✕</Button>
          </div>

          {/* Info do Vendedor */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">👤</span>
            </div>
            <h3 className="font-semibold text-lg">{sellerName}</h3>
          </div>

          {/* Estrelas */}
          <div className="flex justify-center space-x-3 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={(e) => { e.stopPropagation(); setRating(star); }}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="text-4xl transition-transform hover:scale-110 focus:outline-none"
              >
                {star <= (hoverRating || rating) ? '⭐' : '☆'}
              </button>
            ))}
          </div>
          
          {/* Indicador de estrelas selecionadas (sem julgamento) */}
          <p className="text-center text-sm text-gray-500 mb-4">
            {rating > 0 ? `${rating} de 5 estrelas` : 'Toque nas estrelas para avaliar'}
          </p>
          
          {/* Comentário */}
          <Textarea
            placeholder="Deixe um comentário (opcional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="mb-4"
            rows={3}
          />
          
          {/* Feedback Visual */}
          {feedback.message && (
            <div className={`mb-4 p-3 rounded-lg text-center ${
              feedback.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {feedback.type === 'success' ? '✅' : '⚠️'} {feedback.message}
            </div>
          )}

          {/* Botões */}
          <div className="flex space-x-3">
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || rating === 0 || feedback.type === 'success'}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white"
            >
              {isSubmitting ? t('sending') : t('sendEvaluation')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseModal}
            >
              {feedback.type === 'success' ? t('close') : t('cancel')}
            </Button>
          </div>
        </div>
      </Card>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="mt-3 w-full text-yellow-600 border-yellow-300 hover:bg-yellow-50"
        onClick={handleOpenModal}
      >
        ⭐ Avaliar Vendedor
      </Button>
      {modalContent}
    </>
  );
};

export default RateSellerButton;
